import { when } from 'mobx';
import { useState } from 'react';

import { useStore } from '@feathr/extender/state';
import { logError } from '@feathr/hooks';

/**
 * Encapsulates a one-shot check for whether the account has any pending contact imports.
 *
 * @returns An object exposing `checkPendingImports`, which resolves once the imports request
 * settles.
 */
export function useHasPendingImports(): { checkPendingImports: () => Promise<boolean> } {
  const { Importers } = useStore();

  /**
   * Lists pending person imports for the account and reports whether any exist.
   *
   * @returns `true` when at least one person import is in the `pending` state. Returns `false`
   * if the request errors, so a failed check never blocks publishing.
   */
  async function checkPendingImports(): Promise<boolean> {
    const pendingImports = Importers.list(
      {
        filters: {
          kind: 'person',
          state: 'pending',
        },
      },
      { reset: true },
    );
    await when(() => !pendingImports.isPending);
    /*
     * Never block publishing on a failed check: if the imports request errors, treat the
     * account as having no pending imports (skip the warning) and log for support visibility.
     */
    if (pendingImports.isErrored) {
      logError({
        extra: { error: pendingImports.error?.message },
        level: 'warning',
        message: 'Pending imports check failed; skipping pre-publish warning',
      });
      return false;
    }
    return pendingImports.models.length > 0;
  }

  return { checkPendingImports };
}

/**
 * Return value of {@link usePendingImportsPublishGuard}.
 */
export interface IPendingImportsPublishGuard {
  /** Whether the pending-imports warning modal is open. */
  isPendingImportsModalOpen: boolean;
  /** Closes the warning modal without publishing. */
  closePendingImportsModal: () => void;
  /**
   * Entry point for a publish attempt. Publishes immediately when there are no pending imports;
   * otherwise opens the warning modal so the user can decide.
   */
  requestPublish: () => Promise<void>;
  /** Dismisses the warning modal and publishes anyway. */
  publishAnyway: () => Promise<void>;
  /**
   * True while a publish attempt is in flight: imports check, awaiting the user's modal decision,
   * or the wrapped publish call. Wire into the publish button's `disabled` to block double-clicks.
   */
  isPublishing: boolean;
}

/**
 * Guards a campaign publish action behind a pending-imports check. Wrap the flow's real publish
 * action with this hook so each campaign edit component stays small.
 *
 * @param publish - The action that actually publishes the campaign.
 * @returns Modal state and handlers wiring the warning modal into the publish flow.
 *
 * @example
 * const { isPendingImportsModalOpen, closePendingImportsModal, requestPublish, publishAnyway } =
 *   usePendingImportsPublishGuard(publish);
 */
export function usePendingImportsPublishGuard(
  publish: () => Promise<void>,
): IPendingImportsPublishGuard {
  const { checkPendingImports } = useHasPendingImports();
  const [isPendingImportsModalOpen, setIsPendingImportsModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  async function requestPublish(): Promise<void> {
    setIsPublishing(true);
    const hasPendingImports = await checkPendingImports();
    if (hasPendingImports) {
      setIsPendingImportsModalOpen(true);
      return;
    }
    try {
      await publish();
    } finally {
      setIsPublishing(false);
    }
  }

  function closePendingImportsModal(): void {
    setIsPendingImportsModalOpen(false);
    setIsPublishing(false);
  }

  async function publishAnyway(): Promise<void> {
    setIsPendingImportsModalOpen(false);
    try {
      await publish();
    } finally {
      setIsPublishing(false);
    }
  }

  return {
    closePendingImportsModal,
    isPendingImportsModalOpen,
    isPublishing,
    publishAnyway,
    requestPublish,
  };
}
